import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import EmailConfirmationModal from './EmailConfirmationModal';

const AdminCreation = ({ userInfo, onLogout }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [accountType, setAccountType] = useState('admin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Email confirmation modal state
  const [emailConfirmation, setEmailConfirmation] = useState({
    isOpen: false,
    emailData: null,
    onConfirm: null
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/companies`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleCompanyToggle = (companyName) => {
    setAssignedCompanies(prev => {
      if (prev.includes(companyName)) {
        return prev.filter(c => c !== companyName);
      } else {
        return [...prev, companyName];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Build email confirmation data
    const roleDisplay = accountType === 'superadmin' ? 'Super Admin' : 'Admin';
    const emailData = {
      subject: `${roleDisplay} Invitation`,
      recipients: [{
        email: email,
        role: roleDisplay
      }],
      description: `Inviting new ${roleDisplay} user`,
      additionalInfo: accountType === 'admin' && assignedCompanies.length > 0
        ? `This admin will have access to: ${assignedCompanies.join(', ')}`
        : accountType === 'superadmin'
        ? 'This user will have full system access and can manage all companies.'
        : 'This admin will not have access to any companies until assigned.'
    };

    // Show email confirmation modal
    setEmailConfirmation({
      isOpen: true,
      emailData,
      onConfirm: () => submitAdminInvitation()
    });
  };

  const submitAdminInvitation = async () => {
    setLoading(true);
    setEmailConfirmation(prev => ({ ...prev, isOpen: false }));

    try {
      const response = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/invite-admin`,
        {
          email,
          assignedCompanies,
          accountType
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(`${accountType === 'superadmin' ? 'Super Admin' : 'Admin'} invitation sent to ${email} successfully! They will receive an email to set up their account.`);
        setEmail('');
        setAssignedCompanies([]);
        setAccountType('admin');
      } else {
        setError(data.error || 'Failed to send admin invitation');
      }
    } catch (error) {
      console.error('Error sending admin invitation:', error);
      setError('Failed to send admin invitation');
    }

    setLoading(false);
  };

  // Only show for super admin users
  if (userInfo?.accountType !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">Admin invitation is only available for super admin users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="card p-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/company-management', { state: { activeTab: 'admins' } })}
                className="mr-4 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                ← Back to Dashboard
              </button>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite Admin User</h2>
                <p className="text-gray-600">Send an invitation to create a new admin user. They will receive an email to set up their account and password.</p>
              </div>
            </div>
            {/* <button 
              onClick={onLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Logout
            </button> */}
          </div>
        </div>
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        <div className="card p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field mt-1"
                placeholder="Enter admin email address"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                An invitation email will be sent to this address with setup instructions.
              </p>
            </div>


            <div>
              <label htmlFor="accountType" className="block text-sm font-medium text-gray-700">
                Account Type
              </label>
              <select
                id="accountType"
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                className="input-field mt-1"
              >
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Super Admins have full system access and can create other admins. Regular Admins can only manage assigned companies.
              </p>
            </div>

            {accountType === 'admin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Assign Companies (Optional)
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {companies.length === 0 ? (
                    <p className="text-sm text-gray-500">No companies available</p>
                  ) : (
                    companies.map((company) => (
                      <label key={company.name} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={assignedCompanies.includes(company.name)}
                          onChange={() => handleCompanyToggle(company.name)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{company.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select companies this admin can manage. Leave empty for no access to any companies.
                </p>
              </div>
            )}

            {accountType === 'superadmin' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  <strong>Super Admin:</strong> This user will have full system access and can manage all companies and create other administrators. They will receive an invitation email to set up their account.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary flex-1 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Sending Invitation...' : 'Send Admin Invitation'}
              </button>
            </div>
          </form>
        </div>

        {assignedCompanies.length > 0 && (
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Selected Companies</h3>
            <div className="flex flex-wrap gap-2">
              {assignedCompanies.map((company) => (
                <span 
                  key={company}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {company}
                  <button
                    type="button"
                    onClick={() => handleCompanyToggle(company)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Email Confirmation Modal */}
        <EmailConfirmationModal
          isOpen={emailConfirmation.isOpen}
          onClose={() => setEmailConfirmation({ isOpen: false, emailData: null, onConfirm: null })}
          onConfirm={emailConfirmation.onConfirm}
          emailData={emailConfirmation.emailData}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default AdminCreation;